import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import ContactForm from "@/components/ContactForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import {
  LocationChoiceBlock,
  LocationFaqs,
  LocationFinalCta,
  LocationProofStrip,
  LocationSupportAndReviews,
} from "@/components/LocationSharedBlocks";
import { IndustryRelatedLinks, IndustrySections } from "@/components/IndustrySections";
import {
  CHOICE_BLOCK,
  FINAL_CTA,
  FORM_INTRO,
  INDUSTRIES,
  PROOF_STRIP,
  REVIEW_BLOCK,
  SUPPORT_BLOCK,
  plainText,
} from "@/data/industriesContent";
import { fadeUp, stagger, heroImageMotion } from "@/utils/motionVariants";

/**
 * /industries/{sector} — ten sector pages (handover v3, Sep 2026).
 *
 * Section order is fixed by the approved copy:
 *   hero → two customer-choice cards → proof strip → sector sections
 *   (cards / prose / named examples, in copy order) → named-person support +
 *   customer-review link → five FAQs → related links → retained enquiry
 *   form → final CTA.
 *
 * Copy lives in data/industriesContent.js. The enquiry form is the existing
 * ContactForm, unchanged — the handover's proposed button/response wording
 * needs the form owner's sign-off before it replaces the current labels.
 */

export default function IndustryPage() {
  const { industry } = useParams();
  const data = INDUSTRIES[industry];
  if (!data) return <Navigate to="/industries" replace />;

  const path = `/industries/${industry}`;

  return (
    <div data-testid={`${industry}-page`}>
      <SEOHead
        rawTitle={data.seoTitle}
        description={data.metaDescription}
        path={path}
        faqs={data.faqs.map((f) => ({ q: f.q, a: plainText(f.a) }))}
        service={{
          name: data.h1,
          description: data.metaDescription,
          serviceType: "Merchant Services",
        }}
      />

      {/* 1. Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid={`${industry}-hero`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" labels={{ [industry]: data.breadcrumb }} />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-4">
                {data.eyebrow}
              </motion.p>
              <motion.h1
                variants={fadeUp}
                className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6"
              >
                {data.h1}
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">
                {data.heroBody}
              </motion.p>
            </motion.div>
            <motion.div {...heroImageMotion} className="hidden lg:block">
              <img src={data.img} alt="" className="w-full rounded-2xl shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2 + 3. Customer-choice cards and qualified proof strip */}
      <LocationChoiceBlock content={data.choice || CHOICE_BLOCK} testId={`${industry}-choice`} />
      <LocationProofStrip content={data.proof || PROOF_STRIP} testId={`${industry}-proof-strip`} />

      {/* 4. Sector sections */}
      <IndustrySections sections={data.sections} testIdPrefix={industry} />

      {/* 5. Named-person support + customer-review link */}
      <LocationSupportAndReviews
        support={SUPPORT_BLOCK}
        review={REVIEW_BLOCK}
        bg={data.sections.length % 2 === 1 ? "bg-gray-50" : "bg-white"}
        testId={`${industry}-support`}
      />

      {/* 6. Five FAQs */}
      <LocationFaqs heading={data.faqHeading} faqs={data.faqs} testIdPrefix={`${industry}-faq`} />

      {/* 7. Related links */}
      <IndustryRelatedLinks links={data.related} testId={`${industry}-related`} />

      {/* 8. Retained enquiry form */}
      <section className="py-14 md:py-16 bg-white" data-testid={`${industry}-enquiry`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">{data.formHeading}</h2>
              <p className="text-sm text-brand-grey leading-relaxed">{FORM_INTRO}</p>
            </div>
            <div className="lg:col-span-3 bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <ContactForm context={{ industryLabel: data.breadcrumb, industry: industry }} />
            </div>
          </div>
        </div>
      </section>

      {/* 9. Final CTA */}
      <LocationFinalCta content={data.finalCta || FINAL_CTA} testId={`${industry}-final-cta`} />
    </div>
  );
}
